import React from 'react';
import { useData } from 'vike-react/useData';
import type { HomepageData } from '../pages/index/+data';
import HeaderSSR from './HeaderSSR';
import { CurrencyRateNote } from '../../components/CurrencyToggle';
import { HomeHero, MobileSearchRow, formatCount } from './home/HeroSearch';
import SpotlightRow from './home/SpotlightRow';
import NewThisWeekRow from './home/NewThisWeekRow';
import AreasRow, { type HomeAreaTile } from './home/AreasRow';
import GuidesTeaser from './home/GuidesTeaser';
import HomeFooter from './home/HomeFooter';
import RowHeader from './home/RowHeader';
import HomeLink from './home/HomeLink';
import ListingCard from './home/ListingCard';
import { listingViewFromSsrRow, listingViewFromSyndicated, savedKeyFromView } from './home/listingView';
import { homeFirstBatchViews } from './home/homeFirstBatch';
import { BOTTOM_NAV_CLEARANCE, CONTENT_PAD, DISPLAY_FONT } from './home/tokens';
import { HOME_HERO_IMAGE_URL, areaImageUrl } from '../../config/homeImages';

/**
 * HOME-01 — the server-rendered Home/Discover page.
 *
 * WHAT THIS IS FOR. It is the HTML Google indexes and the first paint a cold
 * visitor sees, before App.tsx has downloaded. A moment later the SPA hydrates
 * over it (src/pages/index/+Page.tsx) and pages/Home.tsx takes the page over
 * with the same modules and real interactivity. So this file's job is to be
 * complete, crawlable and visually identical — not interactive.
 *
 * WHAT IT DELIBERATELY DOES NOT DO, and why nothing here can desync on hydrate:
 *   * No localStorage. "Continue where you left off" and the signed-out prompt
 *     read browser storage, so they are absent here and appear after hydration.
 *     That is the specified behaviour — both modules collapse entirely when
 *     they have nothing — rather than a compromise.
 *   * No save state. Nothing is saved for an unauthenticated server render, so
 *     hearts render unsaved and route to /auth. The SPA takes over from there.
 *   * No currency preference. CurrencyProvider renders KES-only and hides the
 *     toggle until a fresh rate loads (src/pages/+Layout.tsx), so the server
 *     never emits a USD figure that the client might disagree with.
 *
 * THE COUNT. One number, `totalListings + totalSyndicated`, printed in the hero
 * proof card, the mobile search field, the All-properties counter and the "Show
 * all" button. It matches the SPA's `displayableTotal` exactly, so the figure
 * does not move when the page hydrates underneath the reader.
 *
 * THE GRID (SYND-21). All properties shows the feed's first batch — 20
 * marketplace and 10 indexed cards in blocks of four and two — composed on the
 * server by the SPA's own function from the same rows (see
 * src/components/home/homeFirstBatch.ts), so the same 30 cards stand in the
 * same order before and after hydration. "Showing N of M" counts both halves.
 *
 * SALE ONLY. Every heading and every count on this page says "for sale". There
 * is no rent path in the product and no rental terminology in this file.
 */

export default function HomeDiscoverSSR() {
  const data = useData<HomepageData>();

  // One total for the whole page — see the header comment.
  const totalForSale = data.totalListings + data.totalSyndicated;

  const spotlight = data.spotlightCarousel.map((row) =>
    listingViewFromSsrRow({ ...row, is_spotlight: true })
  );
  // Marketplace rows only: New this week's marketplace block.
  const feed = data.listings.map(listingViewFromSsrRow);
  // SYND-18: the indexed half of "New this week", and ONLY of that row. `?? []`
  // because a page cached before this field existed hands back data without it.
  const newIndexed = (data.newIndexed ?? []).map(listingViewFromSyndicated);
  // SYND-21: the All-properties grid — the feed's first batch, both halves, in
  // the SPA's order. It drives "Showing N of M", the grid and "Show all".
  const firstBatch = homeFirstBatchViews(data);

  // Top five by the aggregate's own ranking — not a hardcoded list, and not
  // whichever five the query happened to return first.
  const areas: HomeAreaTile[] = data.topNeighborhoods.slice(0, 5).map((n) => ({
    name: n.name,
    count: n.count,
    imageUrl: areaImageUrl(n.name),
  }));

  // Nothing is saved for an anonymous server render, so the set is empty and
  // every heart renders unsaved. `noop` because a server-rendered heart has
  // nothing to toggle; the SPA replaces this whole tree with a live one.
  const noSaves: ReadonlySet<string> = new Set<string>();
  const noop = () => {};

  return (
    <div className={`min-h-screen bg-[#F7F6F1] ${BOTTOM_NAV_CLEARANCE}`}>
      <HeaderSSR showCurrencyToggle wide />

      <HomeHero count={totalForSale} heroImageUrl={HOME_HERO_IMAGE_URL} />
      <MobileSearchRow count={totalForSale} />

      <div className={CONTENT_PAD}>
        <SpotlightRow listings={spotlight} savedIds={noSaves} onSave={noop} />

        <section className="pt-7 lg:pt-10">
          <h2
            className="m-0 mb-3.5 text-[26px] font-extrabold tracking-[-0.02em] text-[#2D2D2D] lg:mb-[18px] lg:text-[32px]"
            style={{ fontFamily: DISPLAY_FONT }}
          >
            Discover
          </h2>

          <div className="flex flex-col gap-7">
            <NewThisWeekRow marketplace={feed} indexed={newIndexed} savedIds={noSaves} onSave={noop} />
            {/*
              "Most viewed" is absent by design, not by accident — no per-listing
              view counter exists that a buyer surface may read. See
              src/components/home/MostViewedRow.tsx and HOME-02.
            */}
            <AreasRow areas={areas} />
          </div>
        </section>

        <GuidesTeaser guides={data.guides} />

        <section className="pt-11 pb-14">
          <RowHeader title="All properties" level="section" className="mb-2" />
          <p className="mb-4 text-[13px] text-[#666666]">
            Showing {formatCount(firstBatch.length)} of {formatCount(totalForSale)} properties
          </p>
          <CurrencyRateNote className="mb-4" />

          {/*
            HOME-01 fix round 1 — the redesign's own card here too, so the
            server render and the hydrated SPA show the same card rather than
            two that merely resemble each other. `noop`/`noSaves` because
            nothing is saved for an anonymous server render; the SPA replaces
            this tree with a live one a moment later.
          */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-[18px]">
            {/* Keys are source-prefixed: the grid holds rows from two tables,
                and their uuids are not one id space. */}
            {firstBatch.map((listing, index) => (
              <ListingCard
                key={savedKeyFromView(listing)}
                listing={listing}
                variant="grid"
                saved={false}
                onSave={noop}
                isLCP={index === 0}
              />
            ))}
          </div>

          {totalForSale > firstBatch.length && (
            <HomeLink
              to="/"
              className="mt-3.5 block w-full rounded-[10px] border-[1.5px] border-[#134025] px-4 py-3 text-center text-[14px] font-bold text-[#134025] lg:hidden"
            >
              Show all {formatCount(totalForSale)} homes
            </HomeLink>
          )}
        </section>
      </div>

      <HomeFooter />
    </div>
  );
}
